import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { customerCipaService } from '../../services/cipa.service';
import {
  ShieldCheck,
  Users,
  Vote,
  CheckCircle2,
  Lock,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import type {
  CipaCandidaturaContract,
  CipaEleitorStatusContract,
  CipaMembroContract,
  CipaProcessoEleitoralContract,
  ColaboradorPortalPerfilContract,
  ComprovanteVotoContract,
  InscreverCandidaturaDto,
  RegistrarVotoDto,
} from '@sstexames/shared-types';

interface CipaColaboradorTabProps {
  perfil: ColaboradorPortalPerfilContract | null;
}

export default function CipaColaboradorTab({ perfil }: CipaColaboradorTabProps) {
  const [loading, setLoading] = useState(false);
  const [processo, setProcesso] = useState<CipaProcessoEleitoralContract | null>(null);
  const [candidatos, setCandidatos] = useState<CipaCandidaturaContract[]>([]);
  const [membros, setMembros] = useState<CipaMembroContract[]>([]);
  const [eleitorStatus, setEleitorStatus] = useState<CipaEleitorStatusContract | null>(null);

  // Votação State
  const [votoSelecionado, setVotoSelecionado] = useState<number | 'BRANCO' | 'NULO' | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [votando, setVotando] = useState(false);
  const [comprovanteVoto, setComprovanteVoto] = useState<ComprovanteVotoContract | null>(null);

  // Candidatura State
  const [showCandidaturaModal, setShowCandidaturaModal] = useState(false);
  const [proposta, setProposta] = useState('');
  const [salvandoCandidatura, setSalvandoCandidatura] = useState(false);
  const [candidaturaSucesso, setCandidaturaSucesso] = useState(false);

  // Carregar dados gerais da CIPA
  const loadCipaData = useCallback(async () => {
    try {
      setLoading(true);
      const [mandatoAtual, membrosData] = await Promise.all([
        customerCipaService.getMandatoAtual(),
        customerCipaService.getMembros(),
      ]);

      setMembros(membrosData);

      if (mandatoAtual?.id) {
        const proc = await customerCipaService.getProcessoEleitoral(mandatoAtual.id);
        setProcesso(proc);

        if (proc?.id) {
          const publicData = await customerCipaService.getProcessoPublico(proc.id);
          setCandidatos((publicData.candidatos || []).filter((c) => c.status === 'HOMOLOGADA'));

          if (perfil?.id) {
            const status = await customerCipaService.consultarStatusEleitor(proc.id, perfil.id);
            setEleitorStatus(status);
            if (status.votou && status.comprovante_hash) {
              setComprovanteVoto({
                processo_id: proc.id,
                protocolo_comprovante: `CIP-${proc.id}-${perfil.id}`,
                autenticacao_sha256: status.comprovante_hash,
                data_hora: status.votou_em || new Date().toISOString(),
                mensagem: 'Voto já registrado na urna eletrônica desta eleição.',
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Erro ao carregar dados da CIPA para o colaborador:', err);
    } finally {
      setLoading(false);
    }
  }, [perfil?.id]);

  useEffect(() => {
    loadCipaData();
  }, [loadCipaData]);

  // Enviar Voto
  const handleConfirmarVoto = async () => {
    if (!processo || !perfil || votoSelecionado === null) return;

    try {
      setVotando(true);
      const isBranco = votoSelecionado === 'BRANCO';
      const isNulo = votoSelecionado === 'NULO';
      const candidatoId = !isBranco && !isNulo ? Number(votoSelecionado) : null;

      const dto: RegistrarVotoDto = {
        processo_id: processo.id,
        colaborador_id: perfil.id,
        candidato_id: candidatoId,
        tipo_voto: isBranco ? 'BRANCO' : isNulo ? 'NULO' : 'VALIDO',
      };

      const result = await customerCipaService.votar(dto);
      setComprovanteVoto(result);
      setShowConfirmModal(false);
      setEleitorStatus((prev) => (prev ? { ...prev, votou: true } : null));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao registrar voto na urna.';
      alert(msg);
    } finally {
      setVotando(false);
    }
  };

  // Enviar Candidatura
  const handleInscreverCandidatura = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!processo || !perfil) return;

    try {
      setSalvandoCandidatura(true);
      const dto: InscreverCandidaturaDto = {
        processo_id: processo.id,
        colaborador_id: perfil.id,
        proposta_resumida: proposta.trim() || undefined,
      };
      await customerCipaService.inscreverCandidatura(dto);
      setCandidaturaSucesso(true);
      setShowCandidaturaModal(false);
      loadCipaData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao registrar candidatura.';
      alert(msg);
    } finally {
      setSalvandoCandidatura(false);
    }
  };

  // Candidato Selecionado Objeto
  const candidatoEscolhido = useMemo(() => {
    if (votoSelecionado === 'BRANCO') return 'Voto em Branco';
    if (votoSelecionado === 'NULO') return 'Voto Nulo';
    if (typeof votoSelecionado === 'number') {
      const c = candidatos.find((cand) => cand.id === votoSelecionado);
      return c?.colaborador_nome || `Candidato #${votoSelecionado}`;
    }
    return null;
  }, [votoSelecionado, candidatos]);

  // Se o colaborador já é candidato
  const jaECandidato = useMemo(() => {
    if (!perfil?.id) return false;
    return candidatos.some((c) => c.colaborador_id === perfil.id);
  }, [candidatos, perfil?.id]);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 animate-fade-in">
      {/* Header do Módulo */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-200/80 shadow-xs">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900 leading-tight">
                CIPA & Comissões (NR-5)
              </h2>
              <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                Participação do Colaborador
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Urna eletrônica com sigilo absoluto, candidaturas e representação da unidade
            </p>
          </div>
        </div>

        <button
          onClick={loadCipaData}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors self-end sm:self-center"
          title="Recarregar"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Alerta de Criptografia & Sigilo do Voto */}
      <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-xs text-emerald-900 leading-relaxed">
          <strong className="font-bold">Garantia Constitucional & NR-5 de Sigilo:</strong> O sistema de votação digital da SST Exames utiliza segregação criptográfica estrita (SHA-256). A confirmação da sua participação como eleitor é separada do conteúdo do voto na urna, tornando impossível a qualquer pessoa identificar a sua escolha.
        </div>
      </div>

      {/* SEÇÃO 1: ELEIÇÃO ATIVA / URNA ELETRÔNICA */}
      {processo ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
          <div className="p-5 border-b border-gray-200 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Vote className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-bold text-gray-800">
                  Eleição da CIPA • Gestão {processo.mandato_ano || new Date().getFullYear()}
                </span>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                  Fase: {processo.status}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Escolha dos representantes dos empregados para a Comissão Interna de Prevenção de Acidentes
              </p>
            </div>

            {/* Ação de Inscrição caso a fase seja INSCRICOES_ABERTAS */}
            {processo.status === 'INSCRICOES_ABERTAS' && !jaECandidato && !candidaturaSucesso && (
              <button
                onClick={() => setShowCandidaturaModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-xs transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Quero me Candidatar à CIPA
              </button>
            )}

            {processo.status === 'INSCRICOES_ABERTAS' && (jaECandidato || candidaturaSucesso) && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Sua Candidatura está Registrada
              </span>
            )}
          </div>

          <div className="p-5 space-y-6">
            {/* ETAPA DE INSCRIÇÕES ABERTAS */}
            {processo.status === 'INSCRICOES_ABERTAS' && (
              <div className="space-y-4">
                <div className="p-4 bg-teal-50/60 rounded-xl border border-teal-200 text-xs text-teal-900 leading-relaxed">
                  <strong>Período de Inscrições Aberto!</strong> Todos os empregados vinculados à unidade podem concorrer livremente a uma vaga na CIPA. Os titulares e suplentes eleitos contam com garantia de emprego (estabilidade CLT Art. 165).
                </div>

                <div>
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-3">
                    Candidatos Homologados até o momento ({candidatos.length})
                  </h3>

                  {candidatos.length === 0 ? (
                    <div className="text-center py-8 text-xs text-gray-400">
                      Nenhum candidato homologado ainda. Seja o primeiro a se inscrever!
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {candidatos.map((c) => (
                        <div
                          key={c.id}
                          className="p-3.5 rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-white transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                              {c.colaborador_nome?.charAt(0) || 'C'}
                            </div>
                            <div>
                              <span className="text-xs font-bold text-gray-900 block">
                                {c.colaborador_nome}
                              </span>
                              <span className="text-[11px] text-gray-500">
                                {c.colaborador_cargo || 'Cargo não informado'}
                              </span>
                            </div>
                          </div>
                          {c.proposta_resumida && (
                            <p className="mt-2 text-xs text-gray-600 italic bg-white p-2 rounded-lg border border-gray-100">
                              &ldquo;{c.proposta_resumida}&rdquo;
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ETAPA DE VOTAÇÃO: URNA ELETRÔNICA BLINDADA */}
            {processo.status === 'VOTACAO_ABERTA' && (
              <div className="space-y-6">
                {comprovanteVoto || eleitorStatus?.votou ? (
                  /* Tela de Comprovante de Voto */
                  <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-6 text-center space-y-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">
                        Voto Registrado com Sucesso!
                      </h3>
                      <p className="text-xs text-gray-600 max-w-md mx-auto mt-1">
                        Sua participação no processo eleitoral foi computada com segurança e segregação criptográfica de ponta a ponta.
                      </p>
                    </div>

                    {comprovanteVoto && (
                      <div className="bg-white rounded-xl p-4 border border-emerald-200 max-w-lg mx-auto text-left space-y-2 text-xs">
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                          <span className="font-bold text-gray-800">Comprovante de Votação</span>
                          <span className="text-emerald-700 font-semibold text-[11px]">
                            {new Date(comprovanteVoto.data_hora).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400 block text-[10px] font-mono">
                            HASH CRIPTOGRÁFICO DE VERIFICAÇÃO (SHA-256):
                          </span>
                          <div className="font-mono text-[11px] text-gray-800 break-all bg-gray-50 p-2 rounded border border-gray-200">
                            {comprovanteVoto.autenticacao_sha256}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Cabine de Votação Secreta */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                        Selecione seu Candidato para a CIPA
                      </h3>
                      <span className="text-[11px] text-gray-400">Escolha única</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {candidatos.map((c) => {
                        const isSelected = votoSelecionado === c.id;

                        return (
                          <div
                            key={c.id}
                            onClick={() => setVotoSelecionado(c.id)}
                            className={`p-4 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                              isSelected
                                ? 'bg-emerald-50/80 border-emerald-500 ring-2 ring-emerald-500/20 shadow-xs'
                                : 'bg-white border-gray-200 hover:border-emerald-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs ${
                                  isSelected
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {c.colaborador_nome?.charAt(0) || 'C'}
                              </div>
                              <div className="flex-1">
                                <span className="text-xs font-bold text-gray-900 block">
                                  {c.colaborador_nome}
                                </span>
                                <span className="text-[11px] text-gray-500">
                                  {c.colaborador_cargo || 'Cargo não informado'}
                                </span>
                              </div>
                              <div
                                className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                  isSelected
                                    ? 'border-emerald-600 bg-emerald-600'
                                    : 'border-gray-300'
                                }`}
                              >
                                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                              </div>
                            </div>

                            {c.proposta_resumida && (
                              <p className="mt-3 text-[11px] text-gray-600 bg-gray-50/80 p-2 rounded-lg border border-gray-100 line-clamp-2">
                                &ldquo;{c.proposta_resumida}&rdquo;
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {/* Opção: Voto em Branco */}
                      <div
                        onClick={() => setVotoSelecionado('BRANCO')}
                        className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                          votoSelecionado === 'BRANCO'
                            ? 'bg-amber-50/80 border-amber-500 ring-2 ring-amber-500/20 shadow-xs'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div>
                          <span className="text-xs font-bold text-gray-800 block">
                            Voto em Branco
                          </span>
                          <span className="text-[11px] text-gray-400">Não escolher candidato</span>
                        </div>
                        <div
                          className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                            votoSelecionado === 'BRANCO'
                              ? 'border-amber-600 bg-amber-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {votoSelecionado === 'BRANCO' && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </div>
                      </div>

                      {/* Opção: Voto Nulo */}
                      <div
                        onClick={() => setVotoSelecionado('NULO')}
                        className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                          votoSelecionado === 'NULO'
                            ? 'bg-rose-50/80 border-rose-500 ring-2 ring-rose-500/20 shadow-xs'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div>
                          <span className="text-xs font-bold text-gray-800 block">Voto Nulo</span>
                          <span className="text-[11px] text-gray-400">Anular voto na urna</span>
                        </div>
                        <div
                          className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                            votoSelecionado === 'NULO'
                              ? 'border-rose-600 bg-rose-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {votoSelecionado === 'NULO' && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 flex items-center justify-end">
                      <button
                        type="button"
                        disabled={votoSelecionado === null}
                        onClick={() => setShowConfirmModal(true)}
                        className="px-6 py-2.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        Confirmar Voto Secreto na Urna
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ETAPA DE APURAÇÃO OU HOMOLOGADO */}
            {(processo.status === 'APURACAO' || processo.status === 'HOMOLOGADO' || processo.status === 'CONCLUIDO') && (
              <div className="bg-gray-50 rounded-xl p-6 text-center space-y-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-1" />
                <h4 className="text-sm font-bold text-gray-900">
                  {processo.status === 'APURACAO'
                    ? 'Urna Eletrônica Encerrada — Apuração em Andamento'
                    : 'Processo Eleitoral Concluído e Homologado'}
                </h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  Os votos foram totalizados e os novos membros da comissão já estão empossados para a gestão atual.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* SEÇÃO 2: QUADRO DE REPRESENTANTES DA EMPRESA */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xs p-5 space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                Representantes da CIPA da sua Unidade
              </h3>
              <p className="text-[11px] text-gray-500">
                Seus colegas eleitos e indicados disponíveis para apoio em segurança do trabalho
              </p>
            </div>
          </div>
          <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2.5 py-0.5 rounded-full">
            {membros.length} representantes
          </span>
        </div>

        {membros.length === 0 ? (
          <div className="text-center py-8 text-xs text-gray-400">
            Nenhum membro registrado no quadro vigente.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {membros.map((m) => (
              <div
                key={m.id}
                className="p-3.5 rounded-xl border border-gray-100 bg-gray-50/60 flex items-center justify-between"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-900">
                      {m.colaborador_nome || `Membro #${m.id}`}
                    </span>
                    {m.cargo_cipa === 'PRESIDENTE' && (
                      <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded">
                        Presidente
                      </span>
                    )}
                    {m.cargo_cipa === 'VICE_PRESIDENTE' && (
                      <span className="text-[10px] font-bold text-indigo-800 bg-indigo-100 px-1.5 py-0.2 rounded">
                        Vice-Presidente
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-500 mt-0.5 block">
                    {m.representacao === 'EMPREGADOR' ? 'Empregador' : 'Empregados'} •{' '}
                    {m.tipo_membro} • {m.colaborador_cargo || 'Cargo não informado'}
                  </span>
                </div>

                {m.representacao === 'EMPREGADOS' && (
                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                    Eleito
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal: Confirmar Voto */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md overflow-hidden animate-fade-in">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-emerald-600" />
                <h3 className="text-base font-bold text-gray-900">Confirmação de Voto</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <p className="text-gray-600 leading-relaxed">
                Você está prestes a registrar o seu voto secreto para a CIPA. Verifique a sua escolha:
              </p>

              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
                <span className="text-[11px] text-emerald-800 block">Sua escolha na urna:</span>
                <span className="text-sm font-bold text-emerald-950 mt-1 block">
                  {candidatoEscolhido}
                </span>
              </div>

              <div className="text-[11px] text-gray-500 bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                <strong>Importante:</strong> Após a confirmação, o voto é irreversível e depositado na urna criptografada de forma completamente desvinculada de seus dados pessoais.
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Voltar e Corrigir
              </button>
              <button
                type="button"
                disabled={votando}
                onClick={handleConfirmarVoto}
                className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-xs disabled:opacity-50"
              >
                {votando ? 'Registrando...' : 'Confirmar Voto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Inscrição de Candidatura */}
      {showCandidaturaModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg overflow-hidden animate-fade-in">
            <form onSubmit={handleInscreverCandidatura}>
              <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-base font-bold text-gray-900">
                    Inscrição de Candidatura à CIPA
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCandidaturaModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="p-5 space-y-4 text-xs">
                <div>
                  <span className="text-gray-500 block">Candidato(a):</span>
                  <span className="text-sm font-bold text-gray-900">{perfil?.nome}</span>
                  <span className="text-[11px] text-gray-400 block">{perfil?.cargo || 'Cargo não informado'}</span>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">
                    Sua Mensagem ou Proposta aos Colegas (Opcional)
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ex: Compromisso com fiscalização de EPIs, melhorias na ergonomia e diálogo constante..."
                    value={proposta}
                    onChange={(e) => setProposta(e.target.value)}
                  />
                  <span className="text-[11px] text-gray-400 mt-1 block">
                    Esta mensagem será exibida na cabine de votação para os eleitores.
                  </span>
                </div>

                <div className="p-3 bg-teal-50 rounded-xl border border-teal-200 text-teal-900 text-[11px] leading-relaxed">
                  <strong>Garantia Legal (CLT Art. 165):</strong> A sua inscrição assegura estabilidade provisória no emprego desde o momento do registro até 1 ano após o encerramento do mandato para os eleitos.
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCandidaturaModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvandoCandidatura}
                  className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-xs disabled:opacity-50"
                >
                  {salvandoCandidatura ? 'Inscrevendo...' : 'Confirmar Minha Candidatura'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
